const CHAT_SCROLL_CONTAINER_SELECTOR = "[data-chat-scroll-container]";
const CHAT_ITEM_BODY_SELECTOR = "[data-chat-item-body]";
const STEP_TOP_GUTTER = 12;

export const chatScrollBehavior = (): ScrollBehavior =>
  window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ? "auto"
    : "smooth";

export const scrollChatToBottom = (
  element: HTMLElement | null,
  behavior: ScrollBehavior,
): void => {
  const container = element?.closest<HTMLElement>(
    CHAT_SCROLL_CONTAINER_SELECTOR,
  );
  container?.scrollTo({ top: container.scrollHeight, behavior });
};

/** Follow only a newly revealed timeline tail while its card finishes laying out. */
export const followChatToBottom = (
  end: HTMLElement | null,
  content: HTMLElement | null,
  behavior: ScrollBehavior,
): (() => void) => {
  const container = end?.closest<HTMLElement>(CHAT_SCROLL_CONTAINER_SELECTOR);
  if (!container || !content) return (): void => undefined;

  scrollChatToBottom(end, behavior);
  let lastHeight = container.scrollHeight;
  const observer = new ResizeObserver(() => {
    if (container.scrollHeight === lastHeight) return;
    lastHeight = container.scrollHeight;
    scrollChatToBottom(end, behavior);
  });
  observer.observe(content);

  const stopFollowing = (): void => observer.disconnect();
  const timeout = window.setTimeout(stopFollowing, 1000);
  container.addEventListener("wheel", stopFollowing, {
    once: true,
    passive: true,
  });
  container.addEventListener("touchstart", stopFollowing, {
    once: true,
    passive: true,
  });
  container.addEventListener("pointerdown", stopFollowing, { once: true });
  container.addEventListener("keydown", stopFollowing, { once: true });

  return (): void => {
    window.clearTimeout(timeout);
    stopFollowing();
    container.removeEventListener("wheel", stopFollowing);
    container.removeEventListener("touchstart", stopFollowing);
    container.removeEventListener("pointerdown", stopFollowing);
    container.removeEventListener("keydown", stopFollowing);
  };
};

/** Keep the edited card in view after its controls and the chat layout settle. */
export const revealEditedChatItem = (item: HTMLElement | null): void => {
  const container = item?.closest<HTMLElement>(CHAT_SCROLL_CONTAINER_SELECTOR);
  if (!item || !container) return;

  const itemBounds = item.getBoundingClientRect();
  const containerBounds = container.getBoundingClientRect();
  const topInset = Math.max(
    0,
    (container.clientHeight - itemBounds.height) / 2,
  );
  container.scrollTop += itemBounds.top - containerBounds.top - topInset;

  const actionRows = item.querySelectorAll<HTMLElement>(
    "[data-chat-item-actions]",
  );
  const actions = actionRows[actionRows.length - 1];
  const body = actions?.closest<HTMLElement>(CHAT_ITEM_BODY_SELECTOR);
  if (actions && body) {
    const actionsBounds = actions.getBoundingClientRect();
    const bodyBounds = body.getBoundingClientRect();
    if (actionsBounds.bottom > bodyBounds.bottom) {
      body.scrollTop += actionsBounds.bottom - bodyBounds.bottom;
    }
  }
};

export const revealNewChatStep = (element: HTMLElement | null): void => {
  if (!element) return;

  const behavior = chatScrollBehavior();
  const body = element.closest<HTMLElement>(CHAT_ITEM_BODY_SELECTOR);
  if (body && body.scrollHeight > body.clientHeight + 1) {
    const bodyBounds = body.getBoundingClientRect();
    const stepBounds = element.getBoundingClientRect();
    if (
      stepBounds.top < bodyBounds.top + STEP_TOP_GUTTER ||
      stepBounds.bottom > bodyBounds.bottom - STEP_TOP_GUTTER
    ) {
      body.scrollTo({
        top: body.scrollTop + stepBounds.top - bodyBounds.top - STEP_TOP_GUTTER,
        behavior,
      });
    }
  }
  scrollChatToBottom(element, behavior);
};
